import { UserRole, UserRoles, roleHierarchy } from '../../types/keycloakUsers'

// Define all permissions
export const permissions = {
    viewExperiment: 'view:experiments',
    createExperiment: 'create:experiments',
    updateExperiment: 'update:experiments',
    updateMonitoring: 'update:monitoring',
    editConfigDifferentVariants: 'update:experimentsDifferentVariants',
    setExperimentLive: 'create:liveExperiments',
    addVariants: 'add:variants',
    deleteVariants: 'delete:variants',
} as const

type PermissionType = (typeof permissions)[keyof typeof permissions]

// Base role permission sets
const basePermissions = {
    [UserRoles.EDITOR]: [
        permissions.viewExperiment,
        permissions.createExperiment,
        permissions.updateExperiment,
        permissions.setExperimentLive,
        permissions.addVariants,
        permissions.deleteVariants,
    ],
    [UserRoles.VIEWER]: [permissions.viewExperiment],
}

// Complete role-permission map
const rolePermissions: Record<UserRole, PermissionType[]> = {
    [UserRoles.ADMIN]: Object.values(permissions),
    [UserRoles.EDITOR]: basePermissions[UserRoles.EDITOR],
    [UserRoles.GLOBAL_EDITOR]: basePermissions[UserRoles.EDITOR],
    [UserRoles.VIEWER]: basePermissions[UserRoles.VIEWER],
    [UserRoles.GLOBAL_VIEWER]: basePermissions[UserRoles.VIEWER],
}

// Returns the most privileged *base* role a user has
const getBaseHighestRole = (userRoles: string[]): UserRole | null => {
    for (const role of roleHierarchy) {
        if (userRoles.includes(role)) {
            return role
        }
    }
    return null
}

// Clean, scalable permission check
const hasPermissions = (userRoles: string[], permission: PermissionType): boolean => {
    const baseRole = getBaseHighestRole(userRoles)
    if (!baseRole) return false
    return rolePermissions[baseRole]?.includes(permission) ?? false
}

export default hasPermissions
